import { ColumnDef, CellContext, Row, Cell } from "@tanstack/react-table";
import { RegistrantRow } from "../types";
import { getStickyColumnClasses } from "@/lib/styleHelpers";
import { actionId } from "@/hooks/useTableRowMenu";
import type { Action } from "@/components/MenuButton";
import AvatarDetails from "@/components/AvatarDetails";
import Checkbox from "@/components/Checkbox";
import SegmentedButton from "@/components/SegmentedButton/SegmentedButton";
import IconButton from "@/components/IconButton";
import Icon from "@/components/Icon";
import RegistrantStatusCell from "./RegistrantStatusCell";
import Avatar from "@/components/Avatar";
import { isExcessiveScore } from "../_utils";
import SelectAllHeaderCheckbox from "./SelectAllHeaderCheckbox";
import { buildIconTextColumn } from "@/components/Table";
import { getPATDocumentIconConfig } from "@/features/person/utils/pat";
import React from "react";

export const showHeaderInColumn = "show-header-in-column";

const getDetailsColumnText = (row: RegistrantRow): React.ReactNode => {
  if (row.note) {
    return <span>{row.note}</span>;
  }

  return <span className="opacity-20">--</span>;
};

interface GetColumnsProps {
  hasRegistrations: boolean;
  setMenu: (options: any) => void;
  setUpdateStatusDialogOpen: (open: boolean) => void;
  onAttendanceUpdate?: (id: string, value: string | null) => void;
  onScoreUpdate?: (field: string, id: string, value: string) => void;
  setTargetEventRegistration: (targetEventRegistration: RegistrantRow) => void;
  handleStatusUpdate: (status: string | null, note: string | null, id: string | null) => void;
  isFinalized: boolean;
  canEdit: boolean;
  setShowEditScoreModal: (open: boolean) => void;
  isMobile: boolean;
  isSetupComplete: boolean;
  setSelectedPgaId: (pgaId: string | null) => void;
  setIsPersonSideSheetOpen: (open: boolean) => void;
}

export const getColumns = ({
  hasRegistrations,
  setMenu,
  setUpdateStatusDialogOpen,
  onAttendanceUpdate,
  onScoreUpdate,
  setTargetEventRegistration,
  handleStatusUpdate,
  isFinalized,
  canEdit,
  setShowEditScoreModal,
  isMobile,
  isSetupComplete,
  setSelectedPgaId,
  setIsPersonSideSheetOpen
}: GetColumnsProps): ColumnDef<RegistrantRow, any>[] => [
  ...(!isFinalized
    ? [
        {
          id: "select",
          header: ({ table }: { table: any }) => <SelectAllHeaderCheckbox table={table} isFinalized={isFinalized} />,
          cell: ({ row }: { row: Row<RegistrantRow> }) => (
            <Checkbox
              name={`select-row-${row.id}`}
              checked={row.getIsSelected()}
              disabled={!row.getCanSelect()}
              onChange={(checked) => row.toggleSelected(checked)}
            />
          ),
          enableSorting: false,
          size: 40
        }
      ]
    : []),
  ...(isMobile
    ? [
        {
          cell: (cellProps: CellContext<RegistrantRow, unknown>) => {
            const { profilePhoto } = cellProps.row.original;

            return <Avatar picture={profilePhoto} />;
          },
          id: "avatar"
        },
        {
          cell: (cellProps: CellContext<RegistrantRow, unknown>) => {
            const { registrantName, programType } = cellProps.row.original;
            return (
              <div>
                <div className="text-body-medium">{registrantName}</div>
                <div className="text-body-small text-sys-brand-on-surface-variant">{programType}</div>
              </div>
            );
          },
          id: "registrantName"
        }
      ]
    : [
        {
          cell: (cellProps: CellContext<RegistrantRow, unknown>) => {
            const { profilePhoto, registrantName, programType } = cellProps.row.original;
            return <AvatarDetails picture={profilePhoto} title={registrantName} text={programType} />;
          },
          id: "participant",
          header: "Participant",
          size: 200,
          enableSorting: false,
          accessorFn: (row: RegistrantRow) => row.registrantName,
          meta: {
            className: `${getStickyColumnClasses(hasRegistrations)} z-10 ${!isFinalized ? "pl-2" : ""}`,
            headerClassName: `${!isFinalized ? "-ml-2" : ""}`
          }
        }
      ]),
  {
    accessorKey: "gender",
    header: "Gender",
    meta: {
      headerClassName: showHeaderInColumn
    },
    enableSorting: false
  },
  {
    accessorKey: "age",
    header: "Age",
    meta: {
      headerClassName: showHeaderInColumn
    },
    enableSorting: false
  },
  {
    id: "attendance",
    header: "Attendance",
    enableSorting: false,
    cell: (cellProps: CellContext<RegistrantRow, unknown>) => {
      const { attended, sessionRegistrationId } = cellProps.row.original;

      if (!isSetupComplete) {
        return <span className="opacity-20">{"--"}</span>;
      }

      const handleAttendanceChange = (value: string | null) => {
        const currentValue = attended === null ? null : attended ? "true" : "false";
        if (value === currentValue) {
          onAttendanceUpdate?.(sessionRegistrationId, null);
        } else {
          onAttendanceUpdate?.(sessionRegistrationId, value);
        }
      };

      if (isMobile) {
        return <label className="text-body-small">{attended ? "Present" : "Absent"}</label>;
      }
      return (
        <SegmentedButton
          key={`${sessionRegistrationId}-${attended}`}
          showCheckmark
          allowDeselect
          options={[
            { label: "Present", value: "true" },
            { label: "Absent", value: "false" }
          ]}
          value={attended === null ? null : attended ? "true" : "false"}
          onChange={handleAttendanceChange}
          disabled={isFinalized || !isSetupComplete}
        />
      );
    },
    meta: {
      className: "max-md:hidden",
      headerClassName: showHeaderInColumn
    }
  },
  {
    accessorKey: "score_18_1",
    header: "Round 1",
    enableSorting: false,
    cell: (cellProps: CellContext<RegistrantRow, unknown>) => {
      if (!isSetupComplete) {
        return <span className="opacity-20">{"--"}</span>;
      }
      return cellProps.getValue();
    },
    meta: {
      className: getStickyColumnClasses(hasRegistrations),
      headerClassName: showHeaderInColumn,
      editable: (row: Row<RegistrantRow>) => !isFinalized && isSetupComplete && row.original.attended !== false,
      onEdit: (value: string, cell: Cell<RegistrantRow, unknown>) => {
        onScoreUpdate?.("roundOneScore", cell.row.original.id, value);
      },
      editableIcon: (cell: Cell<RegistrantRow, unknown>) => {
        const { meets_qualifying_score_18_1 } = cell.row.original;
        return meets_qualifying_score_18_1 && <Icon name="check" />;
      }
    }
  },
  {
    accessorKey: "score_18_2",
    header: "Round 2",
    enableSorting: false,
    cell: (cellProps: CellContext<RegistrantRow, unknown>) => {
      if (!isSetupComplete) {
        return <span className="opacity-20">{"--"}</span>;
      }
      return cellProps.getValue();
    },
    meta: {
      className: getStickyColumnClasses(hasRegistrations),
      headerClassName: showHeaderInColumn,
      editable: (row: Row<RegistrantRow>) => !isFinalized && isSetupComplete && row.original.attended !== false,
      onEdit: (value: string, cell: Cell<RegistrantRow, unknown>) => {
        onScoreUpdate?.("roundTwoScore", cell.row.original.id, value);
      },
      editableIcon: (cell: Cell<RegistrantRow, unknown>) => {
        const { meets_qualifying_score_18_2 } = cell.row.original;
        return meets_qualifying_score_18_2 && <Icon name="check" />;
      }
    }
  },
  {
    accessorKey: "score_total",
    header: "Total",
    enableSorting: false,
    meta: {
      className: getStickyColumnClasses(hasRegistrations),
      headerClassName: showHeaderInColumn
    },
    cell: (cellProps: CellContext<RegistrantRow, unknown>) => {
      if (!isSetupComplete) {
        return <span className="opacity-20">{"--"}</span>;
      }
      const { score_total } = cellProps.row.original;
      return <span>{score_total || "--"}</span>;
    }
  },
  {
    accessorKey: "registrantStatus",
    header: "Status",
    enableSorting: false,
    size: 180,
    cell: (cellProps: CellContext<RegistrantRow, unknown>) => {
      if (!isSetupComplete) {
        return <span className="opacity-20">{"--"}</span>;
      }
      return <RegistrantStatusCell row={cellProps.row.original} />;
    }
  },
  buildIconTextColumn({
    accessorKey: "details",
    header: "Details",
    enableSorting: false,
    meta: { skipEmptyState: true },
    getText: getDetailsColumnText,
    getIcon: (row: RegistrantRow) => getPATDocumentIconConfig(row.hasDocument, row.documentUrl, row.note)
  }),
  {
    cell: (cellProps: CellContext<RegistrantRow, unknown>) => {
      const { score_total, attended, pgaId } = cellProps.row.original;

      // Always show expand icon, even if setup isn't complete
      if (!isSetupComplete) {
        return (
          <div className="flex items-center justify-end">
            <IconButton
              data-testid="expand-details"
              title="View person details"
              name="open_in_full"
              iconClassName="text-sys-surface-variant"
              className="!m-0 h-10"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setSelectedPgaId(pgaId);
                setIsPersonSideSheetOpen(true);
              }}
            />
          </div>
        );
      }

      let actions: Action[] = [];

      if (isFinalized && canEdit) {
        actions.push({
          text: "Edit PAT Score",
          onClick: () => {
            setShowEditScoreModal(true);
            setTargetEventRegistration(cellProps.row.original);
          }
        });
      } else if ((attended && !isExcessiveScore(score_total)) || !attended) {
        const { additionalStatus } = cellProps.row.original;
        if (additionalStatus !== "late_cancel") {
          actions.push({
            text: "Update Status",
            onClick: () => {
              setUpdateStatusDialogOpen(true);
              setTargetEventRegistration(cellProps.row.original);
            }
          });
          actions.push({
            text: "Reset Status",
            onClick: () => {
              handleStatusUpdate(null, null, cellProps.row.original.id);
            }
          });
        }
      }

      if (actions.length === 1) {
        return (
          <div
            className={isMobile ? "flex flex-col-reverse items-center" : "flex items-center justify-end"}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <IconButton
              data-testid="expand-details"
              title="View person details"
              name="open_in_full"
              iconClassName="text-sys-surface-variant"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setSelectedPgaId(pgaId);
                setIsPersonSideSheetOpen(true);
              }}
            />
            <IconButton
              id={actionId(cellProps.row.id)}
              onClick={() => {
                setShowEditScoreModal(true);
                setTargetEventRegistration(cellProps.row.original);
              }}
              title="edit-action"
              name="edit"
              skipMargin
            />
          </div>
        );
      }

      return (
        <div
          className={isMobile ? "flex flex-col-reverse items-center" : "flex items-center justify-end"}
          data-testid="menu-icon"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <IconButton
            data-testid="expand-details"
            title="View person details"
            name="open_in_full"
            iconClassName="text-sys-surface-variant"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setSelectedPgaId(pgaId);
              setIsPersonSideSheetOpen(true);
            }}
          />
          {actions.length > 0 && canEdit && (
            <IconButton
              id={actionId(cellProps.row.id)}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setMenu({ rowId: cellProps.row.id, actions });
              }}
              data-testid="actions"
              title="actions"
              name="more_vert"
              className="!m-0 h-10"
            />
          )}
        </div>
      );
    },
    id: "actions",
    header: () => null,
    size: 100
  }
];
